const normalizeAudioHistoryEntry = entry => {
  if (!entry) return null

  if (typeof entry === 'string') {
    return {
      id: null,
      url: entry,
      duration: null,
      timestamp: null,
      source: 'legacy',
      voice_id: null,
      audio_mode: null,
      text_snapshot: ''
    }
  }

  return {
    id: entry.id || null,
    url: entry.url || entry.audio_link || '',
    duration:
      entry.duration != null ? entry.duration : entry.audio_duration || null,
    timestamp: entry.timestamp || null,
    source: entry.source || 'legacy',
    voice_id: entry.voice_id || entry.voiceId || null,
    audio_mode: entry.audio_mode || entry.audioMode || null,
    text_snapshot: entry.text_snapshot || entry.textSnapshot || ''
  }
}

const createAudioHistoryEntry = ({
  audioLink,
  audioDuration,
  source = 'generated',
  voiceId = null,
  audioMode = null,
  textSnapshot = ''
}) => {
  const timestamp = Date.now()

  return {
    id: `audio_${timestamp}_${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    url: audioLink,
    duration: audioDuration != null ? audioDuration : null,
    timestamp,
    source,
    voice_id: voiceId,
    audio_mode: audioMode,
    text_snapshot: textSnapshot || ''
  }
}

const hasHistoryUrl = (history, url) =>
  Boolean(url) && history.some(entry => entry?.url === url)

export const normalizeSceneAudioHistory = scene => {
  const history = Array.isArray(scene?.audio_history)
    ? scene.audio_history
        .map(normalizeAudioHistoryEntry)
        .filter(entry => entry?.url)
    : []

  return {
    ...(scene || {}),
    audio_history: history
  }
}

export const applySceneAudioVersion = (scene, nextAudio = {}, meta = {}) => {
  const normalizedScene = normalizeSceneAudioHistory(scene)
  const history = [...normalizedScene.audio_history]
  const nextAudioLink = nextAudio.audio_link || ''

  if (
    normalizedScene.audio_link &&
    normalizedScene.audio_link !== nextAudioLink &&
    !hasHistoryUrl(history, normalizedScene.audio_link)
  ) {
    history.push(
      createAudioHistoryEntry({
        audioLink: normalizedScene.audio_link,
        audioDuration: normalizedScene.audio_duration,
        source: meta.previousSource || 'previous_current',
        voiceId: meta.previousVoiceId || meta.voiceId || null,
        audioMode: meta.audioMode || null,
        textSnapshot:
          meta.previousTextSnapshot ||
          normalizedScene.aggregated_voiceover ||
          ''
      })
    )
  }

  if (nextAudioLink && !hasHistoryUrl(history, nextAudioLink)) {
    history.push(
      createAudioHistoryEntry({
        audioLink: nextAudioLink,
        audioDuration: nextAudio.audio_duration,
        source: meta.source || 'generated',
        voiceId: meta.voiceId || null,
        audioMode: meta.audioMode || null,
        textSnapshot:
          meta.textSnapshot ||
          nextAudio.aggregated_voiceover ||
          normalizedScene.aggregated_voiceover ||
          ''
      })
    )
  }

  return {
    ...normalizedScene,
    ...nextAudio,
    audio_history: history
  }
}

export const clearSceneCurrentAudio = (
  scene,
  overrides = { audio_duration_estimated: true }
) => {
  const normalizedScene = normalizeSceneAudioHistory(scene)

  return {
    ...normalizedScene,
    audio_link: null,
    audio_duration: null,
    ...overrides
  }
}

export const restoreSceneAudioVersion = (scene, selectedEntry) => {
  const normalizedScene = normalizeSceneAudioHistory(scene)
  const restoredEntry = normalizeAudioHistoryEntry(selectedEntry)

  if (!restoredEntry?.url) return normalizedScene

  const history = [...normalizedScene.audio_history]

  if (
    normalizedScene.audio_link &&
    normalizedScene.audio_link !== restoredEntry.url &&
    !hasHistoryUrl(history, normalizedScene.audio_link)
  ) {
    history.push(
      createAudioHistoryEntry({
        audioLink: normalizedScene.audio_link,
        audioDuration: normalizedScene.audio_duration,
        source: 'previous_current',
        textSnapshot: normalizedScene.aggregated_voiceover || ''
      })
    )
  }

  if (!hasHistoryUrl(history, restoredEntry.url)) {
    history.push(restoredEntry)
  }

  return {
    ...normalizedScene,
    audio_link: restoredEntry.url,
    audio_duration:
      restoredEntry.duration != null
        ? restoredEntry.duration
        : normalizedScene.audio_duration,
    audio_duration_estimated: false,
    audio_history: history
  }
}
